import React, { useState, useMemo } from 'react';
import { CaretUpOutlined, CaretDownOutlined, SearchOutlined, ExpandOutlined, CompressOutlined } from '@ant-design/icons';
import { exportToCSV, exportToExcel, copyToClipboard, ExportButton } from './exportUtils';
import { CHART_PALETTE } from '../../utils/chartColors';

const InteractiveTable = ({
  data = [],
  columns = [],
  title,
  description,
  pageSize = 10,
  searchable = true,
  sortable = true,
  expandable = false,
  expandedRowRender,
  highlightMax = false,
  highlightMin = false,
  showExport = true,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Auto-generate columns from data if not provided
  const tableColumns = useMemo(() => {
    if (columns.length > 0) return columns;
    if (data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      key,
      title: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      dataKey: key,
      sortable: true,
    }));
  }, [columns, data]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase();
    return data.filter((row) =>
      tableColumns.some((col) => {
        const value = row[col.dataKey || col.key];
        return value !== undefined && String(value).toLowerCase().includes(term);
      })
    );
  }, [data, searchTerm, tableColumns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle numeric sorting
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string sorting
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Find max/min values for highlighting
  const numericStats = useMemo(() => {
    const stats = {};
    tableColumns.forEach((col) => {
      const values = data
        .map((row) => row[col.dataKey || col.key])
        .filter((v) => typeof v === 'number');
      if (values.length > 0) {
        stats[col.dataKey || col.key] = {
          max: Math.max(...values),
          min: Math.min(...values),
        };
      }
    });
    return stats;
  }, [data, tableColumns]);

  const handleSort = (columnKey) => {
    if (!sortable) return;
    setSortConfig((prev) => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleRowExpand = (rowIndex) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  };

  const formatValue = (value, column) => {
    if (value === null || value === undefined) return '-';
    if (column.format === 'percent') {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (column.format === 'number') {
      return typeof value === 'number' ? value.toLocaleString() : value;
    }
    if (column.render) {
      return column.render(value);
    }
    return value;
  };

  const getCellStyle = (value, columnKey) => {
    const stats = numericStats[columnKey];
    if (!stats) return {};

    if (highlightMax && value === stats.max) {
      return { backgroundColor: '#dcfce7', fontWeight: 600 };
    }
    if (highlightMin && value === stats.min) {
      return { backgroundColor: '#fee2e2', fontWeight: 600 };
    }
    return {};
  };

  // Export handlers - export filtered/sorted data
  const handleExportCSV = () => {
    const filename = title ? title.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'table_data';
    exportToCSV(sortedData, filename, tableColumns);
  };

  const handleExportExcel = () => {
    const filename = title ? title.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'table_data';
    exportToExcel(sortedData, filename, tableColumns);
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(sortedData, tableColumns);
    if (success) {
      // Could show a toast notification here
      console.log('Data copied to clipboard');
    }
  };

  return (
    <div className="my-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {(title || searchable || showExport) && (
        <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center flex-wrap gap-2">
          <div>
            {title && <h4 className="text-base font-semibold text-gray-900">{title}</h4>}
            {description && <p className="text-sm text-gray-500">{description}</p>}
          </div>
          <div className="flex items-center gap-2">
            {searchable && (
              <div className="relative">
                <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': CHART_PALETTE.purple }}
                />
              </div>
            )}
            {showExport && data.length > 0 && (
              <ExportButton
                onExportCSV={handleExportCSV}
                onExportExcel={handleExportExcel}
                onCopy={handleCopy}
              />
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              {expandable && <th className="w-10 px-2 py-3"></th>}
              {tableColumns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable !== false && handleSort(column.dataKey || column.key)}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                    sortable && column.sortable !== false ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                  }`}
                  style={{ minWidth: column.width }}
                >
                  <div className="flex items-center gap-1">
                    {column.title}
                    {sortable && column.sortable !== false && (
                      <span className="flex flex-col text-gray-300">
                        <CaretUpOutlined
                          style={{
                            fontSize: 10,
                            color:
                              sortConfig.key === (column.dataKey || column.key) &&
                              sortConfig.direction === 'asc'
                                ? CHART_PALETTE.purple
                                : undefined,
                          }}
                        />
                        <CaretDownOutlined
                          style={{
                            fontSize: 10,
                            marginTop: -4,
                            color:
                              sortConfig.key === (column.dataKey || column.key) &&
                              sortConfig.direction === 'desc'
                                ? CHART_PALETTE.purple
                                : undefined,
                          }}
                        />
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedData.map((row, rowIndex) => {
              const isExpanded = expandedRows.has(rowIndex);
              const start = (currentPage - 1) * pageSize;
              const end = start + pageSize;
              const isHidden = rowIndex < start || rowIndex >= end;
              return (
                <React.Fragment key={rowIndex}>
                  <tr
                    className={`hover:bg-gray-50 transition-colors${isHidden ? ' pdf-hidden-row' : ''}`}
                    style={isHidden ? { display: 'none' } : undefined}
                  >
                    {expandable && (
                      <td className="px-2 py-3">
                        <button
                          onClick={() => toggleRowExpand(rowIndex)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          {isExpanded ? (
                            <CompressOutlined className="text-gray-400" />
                          ) : (
                            <ExpandOutlined className="text-gray-400" />
                          )}
                        </button>
                      </td>
                    )}
                    {tableColumns.map((column) => {
                      const value = row[column.dataKey || column.key];
                      return (
                        <td
                          key={column.key}
                          className="px-4 py-3 text-sm text-gray-700"
                          style={getCellStyle(value, column.dataKey || column.key)}
                        >
                          {formatValue(value, column)}
                        </td>
                      );
                    })}
                  </tr>
                  {expandable && isExpanded && expandedRowRender && (
                    <tr>
                      <td colSpan={tableColumns.length + 1} className="px-4 py-3 bg-gray-50">
                        {expandedRowRender(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {paginatedData.length === 0 && (
              <tr>
                <td
                  colSpan={tableColumns.length + (expandable ? 1 : 0)}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center text-sm">
          <span className="text-gray-500">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} results
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 rounded border ${
                    currentPage === pageNum
                      ? 'text-white'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  style={currentPage === pageNum ? { backgroundColor: CHART_PALETTE.purple, borderColor: CHART_PALETTE.purple } : undefined}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveTable;
